import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer',
    {
        variants: {
            variant: {
                default:
                    'shadow-md font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-md transition-colors border-none outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                destructive:
                    'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
                outline:
                    'shadow-md border text-primary-foreground bg-transparent shadow-xs hover:bg-accent dark:bg-input/30 dark:border-input',
                secondary:
                    'shadow-md px-3 py-1.5 text-sm font-medium border-none text-secondary-foreground bg-secondary hover:bg-secondary/80 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                ghost: 'bg-transparent border-none hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 text-foreground',
                link: 'underline-offset-4 underline bg-transparent border-none text-card-foreground',
            },
            size: {
                default: 'h-10 px-6 py-2 has-[>svg]:px-4',
                sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
                lg: 'h-12 rounded-md px-6 has-[>svg]:px-4',
                icon: 'size-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
)

const Button = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> &
        VariantProps<typeof buttonVariants> & {
            asChild?: boolean
        }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
        <Comp
            ref={ref}
            data-slot="button"
            // Sem isto o botão dentro de um `<form>` nasce `type="submit"` e
            // envia o formulário — foi o que fazia o ícone de ajuda associar
            // funcionário. Fora do `asChild`: lá o filho pode ser um `<a>`,
            // que não tem `type`. Quem passa `type` explícito continua manda.
            {...(!asChild && { type: 'button' as const })}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    )
})
Button.displayName = 'Button'

export { Button }
